<template>
  <DropdownMenuRoot>
    <DropdownMenuTrigger
      class="relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-cosmicPurple text-white text-xs font-medium hover:bg-cosmicPurple/90 transition-colors outline-none data-[state=open]:bg-cosmicPurple/90"
    >
      <Loader2 v-if="sending" class="w-3.5 h-3.5 animate-spin" />
      Actions
      <ChevronDown class="w-3.5 h-3.5" />
      <span
        v-if="welcomeReady && !welcomeSent"
        class="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-amber-400 ring-2 ring-white animate-pulse"
        title="Ready to send the welcome email"
      />
    </DropdownMenuTrigger>

    <DropdownMenuPortal>
      <DropdownMenuContent
        align="end"
        :side-offset="6"
        class="z-[70] min-w-[15rem] rounded-lg border border-gray-200 bg-white p-1 shadow-lg outline-none"
      >
        <DropdownMenuLabel
          class="px-2 py-1.5 text-[0.7rem] font-semibold uppercase tracking-wide text-gray-400"
        >Pod</DropdownMenuLabel>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger
            :class="itemClass + ' data-[state=open]:bg-gray-50'"
          >
            <FolderPlus class="w-4 h-4 text-gray-500" />
            <span>Assign to pod</span>
            <ChevronRight class="w-4 h-4 ml-auto text-gray-400" />
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent
              :side-offset="4"
              class="z-[71] max-h-72 overflow-y-auto min-w-[12rem] rounded-lg border border-gray-200 bg-white p-1 shadow-lg outline-none"
            >
              <DropdownMenuItem
                v-for="pod in pods"
                :key="pod.id"
                :class="itemClass"
                @select="emit('assign-pod', pod.id)"
              >
                {{ pod.name }}
              </DropdownMenuItem>
              <div v-if="!pods.length" class="px-2 py-2 text-xs text-gray-400">No pods available</div>
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>

        <DropdownMenuSeparator class="my-1 h-px bg-gray-100" />

        <DropdownMenuLabel
          class="px-2 py-1.5 text-[0.7rem] font-semibold uppercase tracking-wide text-gray-400"
        >Onboarding</DropdownMenuLabel>
        <DropdownMenuItem
          :disabled="welcomeSent || !welcomeReady"
          :class="itemClass"
          @select="emit('send-welcome')"
        >
          <Check v-if="welcomeSent" class="w-4 h-4 text-green-500" />
          <Mail v-else class="w-4 h-4 text-gray-500" />
          <span>{{ welcomeSent ? 'Welcome email sent' : 'Send welcome email' }}</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator class="my-1 h-px bg-gray-100" />

        <DropdownMenuLabel
          class="px-2 py-1.5 text-[0.7rem] font-semibold uppercase tracking-wide text-gray-400"
        >Slack</DropdownMenuLabel>
        <DropdownMenuItem :class="itemClass" @select="emit('edit-slack')">
          <Hash class="w-4 h-4 text-gray-500" />
          <span>{{ hasSlackId ? 'Edit Slack Member ID' : 'Add Slack Member ID' }}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenuPortal>
  </DropdownMenuRoot>
</template>

<script setup lang="ts">
import { Loader2, ChevronDown, ChevronRight, Mail, Check, Hash, FolderPlus } from 'lucide-vue-next'
import {
  DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuPortal,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from 'reka-ui'

interface PodOption { id: number; name: string }

defineProps<{
  welcomeReady: boolean
  welcomeSent: boolean
  hasSlackId: boolean
  sending: boolean
  pods: PodOption[]
}>()

const emit = defineEmits<{
  (e: 'send-welcome'): void
  (e: 'edit-slack'): void
  (e: 'assign-pod', podId: number): void
}>()

const itemClass =
  'flex items-center gap-2 px-2 py-2 text-sm text-gray-700 rounded-md cursor-pointer outline-none ' +
  'data-[highlighted]:bg-gray-50 data-[disabled]:opacity-40 data-[disabled]:cursor-not-allowed'
</script>
